import { useCallback, useMemo } from 'react';
import {
  generateStackedChartExportData,
  exportChartDataToCsv,
  GenerateChartExportDataParams
} from '../utils/chartExportUtils';
import { useAppSelector } from 'src/utils/Hooks';

/**
 * Hook that provides chart data export functionality
 * Extracts and encapsulates the chart export logic from the main component
 */
export const useStackedChartExportData = (params: Omit<GenerateChartExportDataParams, 'chartCategories'>) => {
  const { chartSeries, groupingType, totalEntityTrafficMap, groupedBrandData, getEntityDisplayName } = params;

  // Get date range information from Redux outside the callback
  const { displayName: dateDisplayName } = useAppSelector((state) => state.mainTimePeriod);

  // Memoize chartCategories to prevent recreation on each render
  const chartCategories = useMemo(() => groupedBrandData?.[0]?.keywordOrder || [], [groupedBrandData]);

  // Generate the export data
  const generateExportData = useCallback(() => {
    return generateStackedChartExportData({
      chartSeries,
      chartCategories,
      groupingType,
      totalEntityTrafficMap,
      groupedBrandData,
      getEntityDisplayName
    });
  }, [chartSeries, chartCategories, groupingType, totalEntityTrafficMap, groupedBrandData, getEntityDisplayName]);

  const handleExportChart = useCallback(() => {
    const { headers, rows } = generateExportData();

    return exportChartDataToCsv(headers, rows, groupingType, dateDisplayName);
  }, [dateDisplayName, generateExportData, groupingType]);

  return {
    generateExportData,
    handleExportChart
  };
};
